import React from 'react';

import Tooltip from 'antd/es/tooltip';

import { useBreakpoint } from '@contexts/breakpoints';
import ListingItemSlider from '@components/ui/listing/ListingItemSlider';
import { Col, Row } from '@components/formStyles';
import CustomIcon from '@components/utils/CustomIcon';
import { CheckFill, MapPin2Line, SmilingStar } from '@assets/icons';
import PriceLabel from '@components/ui/labels/price/PriceLabel';
import FreeCancellation from '@components/ui/FreeCancellation/FreeCancellation';
import { FALLBACK_IMAGE_URL } from '@constants/constants';
import { useAppRouter } from '@contexts/router';
import MilePriceBadge from '@uikit/milePriceBadge';
import DiscountLabel from '@components/ui/labels/discount/DiscountLabel';
import { optimizeImage } from '@utils/imageOptimization';
import { getEventDatesInfo } from '@utils/getEventDatesInfo';
import palette from '@utils/theme/palette';
import { RatingProviderType } from '@api/models/RatingProviderType';
import ScoringBanner from '@components/ui/score/Score';
import ExpediaScoringBanner from '@components/ui/score/ExpediaScore';

import { ListingCardProps } from './ListingCardInterface';
import {
  ImageSliderWrapper,
  InformationWrapper,
  Line,
  Title,
  Wrapper,
  Bottom,
  Feature,
  FeatureWrapper,
  SubtitleWrapper,
  IconText,
  SubtitleSeperator,
  FreeCancelletionWrapper,
  DiscountBoxWrapper,
  MileAndPriceWrapper,
  PriceLabelBoxWrapper,
  Badge,
  EventsContainer,
  EventsWrapper,
  EventImage,
  EventContent,
  EventTitle,
  EventDescription,
  HighlightWrapper,
  LocationRatings,
  StarRatingWrapper,
} from './styled';

const ListingCard = ({
  images,
  prices,
  title,
  subtitles,
  person,
  expedia,
  url,
  highlights,
  nightsCount,
  refundable,
  events,
  ratings,
  category,
  priorityImage,
}: ListingCardProps) => {
  const { xl } = useBreakpoint();
  const { push } = useAppRouter();

  const handleNavigation = () => push(url || '');

  // Parse category to extract star count (e.g., "5 Yıldız" -> 5)
  const getStarCount = (categoryString?: string): number => {
    if (!expedia || !categoryString) return 0;
    const match = categoryString.match(/(\d+)\s*Yıldız/i);
    return match ? parseInt(match[1], 10) : 0;
  };

  const starCount = expedia ? getStarCount(category) : 0;

  return (
    <>
      {events?.length ? (
        <Row alignItems='center' justifyContent='flex-end'>
          <Badge>
            <CustomIcon icon={SmilingStar} fontSize={18} />
            Etkinlikli Otel
          </Badge>
        </Row>
      ) : null}
      <Wrapper>
        <ImageSliderWrapper>
          <ListingItemSlider
            images={images}
            fallBackImage={FALLBACK_IMAGE_URL}
            secure={false}
            suggests={false}
            showSliderIcon={xl || xl === undefined}
            onImageClick={handleNavigation}
            lazyLoad={priorityImage ? undefined : 'ondemand'}
            priority={priorityImage}
          />
        </ImageSliderWrapper>

        <InformationWrapper
          onClick={handleNavigation}
          style={{ cursor: 'pointer' }}
        >
          <div>
            <Col
              className='header'
              style={{ height: 120, position: 'relative' }}
            >
              <Row justifyContent='space-between'>
                <Title>{title}</Title>
                {xl && ratings?.rating?.value > 3 ? (
                  <div onClick={(e) => e.stopPropagation()}>
                    <LocationRatings>
                      {ratings?.rating?.value &&
                      ratings.provider === RatingProviderType.TRIP_ADVISOR ? (
                        <ScoringBanner
                          totalScoring={ratings?.rating?.value}
                          reviews={ratings}
                        />
                      ) : null}
                      {ratings?.rating?.value &&
                      ratings.provider === RatingProviderType.EXPEDIA ? (
                        <ExpediaScoringBanner
                          totalScoring={ratings?.rating?.value}
                          reviews={ratings}
                        />
                      ) : null}
                    </LocationRatings>
                  </div>
                ) : null}
              </Row>
              {subtitles?.length ? (
                <SubtitleWrapper>
                  {subtitles?.map((s, i) => (
                    <Row alignItems='center' key={s.text}>
                      {s.icon ? (
                        <CustomIcon
                          icon={MapPin2Line}
                          fontSize={16}
                          paletteColor='grey600'
                        />
                      ) : null}
                      <IconText dark={s?.dark || false}>{s.text}</IconText>
                      {subtitles.length - 1 !== i ? (
                        <SubtitleSeperator />
                      ) : null}
                    </Row>
                  ))}
                </SubtitleWrapper>
              ) : null}

              {starCount > 0 ? (
                <StarRatingWrapper>
                  {Array.from({ length: starCount }, (_, index) => (
                    <img
                      key={index}
                      src='/static/icons/icon-star.svg'
                      alt='star'
                      width={12}
                      height={11}
                    />
                  ))}
                </StarRatingWrapper>
              ) : null}

              <Line />
            </Col>
            {!xl && (ratings?.rating?.value ?? 0) >= 3 ? (
              <div onClick={(e) => e.stopPropagation()}>
                <LocationRatings>
                  {ratings?.rating?.value &&
                  ratings.provider === RatingProviderType.TRIP_ADVISOR ? (
                    <ScoringBanner
                      totalScoring={ratings?.rating?.value}
                      reviews={ratings}
                    />
                  ) : null}
                  {ratings?.rating?.value &&
                  ratings.provider === RatingProviderType.EXPEDIA ? (
                    <ExpediaScoringBanner
                      totalScoring={ratings?.rating?.value}
                      reviews={ratings}
                    />
                  ) : null}
                </LocationRatings>
              </div>
            ) : null}
            <Bottom>
              <Col>
                <Col style={{ height: xl ? 90 : 'auto' }}>
                  <HighlightWrapper>
                    {highlights.map((highlight, i) => (
                      <FeatureWrapper key={i}>
                        <Row alignItems='center'>
                          <CustomIcon
                            icon={CheckFill}
                            fontSize={16}
                            paletteColor='green600'
                          />
                          <Feature>{highlight}</Feature>
                        </Row>
                      </FeatureWrapper>
                    ))}
                  </HighlightWrapper>
                  {refundable ? (
                    <FreeCancelletionWrapper>
                      <FreeCancellation />
                    </FreeCancelletionWrapper>
                  ) : null}
                </Col>

                {events?.length ? (
                  <Col
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    style={{ marginTop: 8, position: 'relative' }}
                  >
                    <b style={{ fontSize: 12 }}>Yaklaşan Etkinlikler</b>
                    <EventsContainer>
                      <Row>
                        {events.map((event) => {
                          const hasNextEvents =
                            getEventDatesInfo(event?.dates).nextEvents.length >
                            0;

                          const eventWrapper = (
                            <EventsWrapper key={event.id}>
                              <EventImage
                                src={optimizeImage(event.image, 50, 70)}
                                alt={event.title}
                                loading='lazy'
                              />
                              <EventContent>
                                <EventTitle
                                  style={{ maxHeight: 45, paddingTop: 4 }}
                                >
                                  {event?.artistName}
                                </EventTitle>
                                <EventDescription>
                                  {getEventDatesInfo(event?.dates).closestEvent}
                                </EventDescription>
                              </EventContent>
                            </EventsWrapper>
                          );

                          return hasNextEvents ? (
                            <Tooltip
                              key={event.id}
                              placement={xl ? 'left' : 'top'}
                              trigger='hover'
                              styles={{
                                body: {
                                  background: 'white',
                                },
                                root: {
                                  maxWidth: 120,
                                  wordWrap: 'break-word',
                                },
                              }}
                              getPopupContainer={(trigger) =>
                                trigger.parentElement || document.body
                              }
                              title={
                                <Col style={{ gap: 4 }}>
                                  <EventTitle
                                    style={{
                                      color: palette.grey1100,
                                      marginBottom: 3,
                                    }}
                                  >
                                    {event?.artistName}
                                  </EventTitle>
                                  <EventDescription
                                    style={{
                                      color: palette.grey1100,
                                      fontWeight: 600,
                                      marginBottom: 6,
                                    }}
                                  >
                                    Diğer tarihler
                                  </EventDescription>
                                  {getEventDatesInfo(
                                    event?.dates
                                  ).nextEvents.map((date, idx) => (
                                    <Row
                                      key={idx}
                                      alignItems='center'
                                      style={{ gap: 6, marginBottom: 2 }}
                                    >
                                      <div
                                        style={{
                                          width: 6,
                                          height: 6,
                                          borderRadius: '50%',
                                          backgroundColor: palette.grey1100,
                                          flexShrink: 0,
                                        }}
                                      />
                                      <EventDescription
                                        style={{
                                          color: palette.grey1100,
                                        }}
                                      >
                                        {date}
                                      </EventDescription>
                                    </Row>
                                  ))}
                                </Col>
                              }
                            >
                              {eventWrapper}
                            </Tooltip>
                          ) : (
                            eventWrapper
                          );
                        })}
                      </Row>
                    </EventsContainer>
                  </Col>
                ) : null}
              </Col>

              <Col>
                {prices ? (
                  <div>
                    {prices && prices.rate ? (
                      <DiscountBoxWrapper>
                        <DiscountLabel discount={prices.rate} />
                      </DiscountBoxWrapper>
                    ) : null}
                    {prices ? (
                      <PriceLabelBoxWrapper>
                        <MileAndPriceWrapper>
                          {prices?.milePrice ? (
                            <MilePriceBadge milePrice={prices?.milePrice} />
                          ) : null}
                          <PriceLabel
                            feeDetails={expedia ? prices.details : undefined}
                            priceWithDiscount={
                              prices && prices.discounted
                                ? prices.discounted
                                : undefined
                            }
                            currency={prices.currency}
                            priceInEUR={
                              expedia ? prices.details?.total : undefined
                            }
                            nightsCount={nightsCount}
                            peopleCount={
                              person.adultCount + person.childCount || 0
                            }
                            originalPrice={
                              prices && prices.rate !== 0 && prices.original
                                ? prices.original
                                : undefined
                            }
                            sellingPrice={prices.discounted}
                          />
                        </MileAndPriceWrapper>
                      </PriceLabelBoxWrapper>
                    ) : null}
                  </div>
                ) : (
                  <div />
                )}
              </Col>
            </Bottom>
          </div>
        </InformationWrapper>
      </Wrapper>
    </>
  );
};

export default ListingCard;
